import { useRef } from 'react';
import { parseISO, format } from 'date-fns';
import { Form, Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { FormThemeContextProvider } from '@components/shared/forms/ThemeContext';
import { SubmitButton } from '@components/shared/forms';
import css from './styles.module.less';
import Amenities from './DeprecatedAmenities';
import BasicInfo from './BasicInfo';
import Certifications from './Certifications';
import Details from './Details';
import Location from './Location';
import PropertyTable from './PropertyTable';
import validationSchema from './validationSchema';
import buildingMetaData from './buildingMetadataBuilder';

const FormikBuildingForm = ({ building, fetchAdminBuilding, updateBuilding }) => {
  const { t } = useTranslation('admin');
  const flags = useFlags();
  const initialValues = {
    ...building,
    owningCompanyId: building.owningCompany?.id || '',
    rentableBuildingArea: building.rentableBuildingArea?.magnitude || '',
    ceilingHeightUnits: building.ceilingHeightUnits || 'imperial',
    rentableBuildingAreaUnit:
      building.rentableBuildingArea?.unit || building.rentableBuildingAreaUnit || '',
    liveAt: building.liveAt ? format(parseISO(building.liveAt), 'M/d/yyyy') : '',
    retiredAt: building.retiredAt ? format(parseISO(building.retiredAt), 'M/d/yyyy') : '',
    metadata: buildingMetaData(building),
  };

  const fiberProviders = useRef(
    initialValues.fiberProviders.map(nt => ({
      label: nt.name,
      value: nt.id,
    })),
  );

  const notableTenants = useRef(
    initialValues.notableTenants.map(nt => ({
      label: nt.companyName,
      value: nt.companyId,
    })),
  );

  return (
    <Formik
      enableReinitialize
      initialValues={JSON.parse(JSON.stringify(initialValues, (k, v) => (v === null ? '' : v)))}
      onSubmit={async (values, actions) => {
        const params = JSON.parse(JSON.stringify(values, (k, v) => (v === '' ? null : v)));

        params.liveAt = params.liveAt ? new Date(params.liveAt) : null;
        params.retiredAt = params.retiredAt ? new Date(params.retiredAt) : null;

        await updateBuilding(params);
        actions.setSubmitting(false);
      }}
      validationSchema={validationSchema(t, building.countryCode)}
    >
      {({ dirty, isValid, values }) => (
        <Form className={css.form}>
          <FormThemeContextProvider theme="vertical">
            <BasicInfo building={building} status={values.status} />
            <Details
              owningCompanyId={values.owningCompany.id}
              owningCompanyName={values.owningCompany.name}
              notableTenants={notableTenants.current}
              rentableBuildingAreaUnit={values.rentableBuildingAreaUnit}
              ceilingHeightUnits={values.ceilingHeightUnits}
              initialValues={initialValues}
            />
            <Location building={building} neighborhoodId={values.neighborhoodId} values={values} />
            {flags['amenities-data-tabs'] ? null : (
              <Amenities
                entranceCount={values.metadata.entranceCount}
                passengerElevatorCount={values.metadata.passengerElevatorCount}
                serviceElevatorCount={values.metadata.serviceElevatorCount}
                passengerElevatorPosition={values.metadata.passengerElevatorPosition}
                twentyFourSevenAccess={values.metadata.twentyFourSevenAccess}
                barAvailable={values.metadata.barAvailable}
                bikeStorageAvailable={values.metadata.bikeStorageAvailable}
                cafeAvailable={values.metadata.cafeAvailable}
                commonAreaWifi={values.metadata.commonAreaWifi}
                communalLobbySpace={values.metadata.communalLobbySpace}
                communalOutdoorSpace={values.metadata.communalOutdoorSpace}
                communalRoofAccess={values.metadata.communalRoofAccess}
                flexSpaceAvailable={values.metadata.flexSpaceAvailable}
                foodServiceAvailable={values.metadata.foodServiceAvailable}
                generalStoreAvailable={values.metadata.generalStoreAvailable}
                gymAvailable={values.metadata.gymAvailable}
                keyCardBuildingEntry={values.metadata.keyCardBuildingEntry}
                landmarkBuilding={values.metadata.landmarkBuilding}
                lobbyAttendant={values.metadata.lobbyAttendant}
                lobbyBeverageAvailable={values.metadata.lobbyBeverageAvailable}
                lobbyFoodAvailable={values.metadata.lobbyFoodAvailable}
                messengerCenter={values.metadata.messengerCenter}
                mobileConnectivityImprovements={values.metadata.mobileConnectivityImprovements}
                optionToAddInternetServiceProvider={
                  values.metadata.optionToAddInternetServiceProvider
                }
                parkingAvailable={values.metadata.parkingAvailable}
                petsAllowed={values.metadata.petsAllowed}
                pharmacyAvailable={values.metadata.pharmacyAvailable}
                propertyManagerOnSite={values.metadata.propertyManagerOnSite}
                restaurantAvailable={values.metadata.restaurantAvailable}
                spaAvailable={values.metadata.spaAvailable}
                storageAvailable={values.metadata.storageAvailable}
                turnstiles={values.metadata.turnstiles}
                urgentCareDoctorOnsite={values.metadata.urgentCareDoctorOnsite}
                yogaStudioAvailable={values.metadata.yogaStudioAvailable}
              />
            )}
            {flags['amenities-data-tabs'] ? null : (
              <Certifications
                fiberProviders={fiberProviders.current}
                leedCertification={values.metadata.leedCertification}
                wiredCertification={values.metadata.wiredCertification}
              />
            )}
            <PropertyTable
              buildingName={building.primaryName}
              buildingId={building.id}
              fetchAdminBuilding={fetchAdminBuilding}
              properties={building.properties}
            />

            {dirty ? (
              <div className={css.submitFooter}>
                <SubmitButton disabled={!isValid}>{t('common:saveChanges')}</SubmitButton>
              </div>
            ) : null}
          </FormThemeContextProvider>
        </Form>
      )}
    </Formik>
  );
};

export default FormikBuildingForm;
